import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import AiJobProgress from '../components/AiJobProgress';
import { cancelAiJob, findActiveBatchJob, waitForAiJob, type AiJob } from '../services/aiJobs';
import '../styles/admin-common.css';

interface Question {
  question_id: string;
  question_text: string;
  options: Array<{ option_number: number; option_text: string }>;
  correct_answers: number[];
  answer_num?: unknown;
  explanation: string;
  reasoning: string;
  keywords?: string[];
  validated?: boolean;
  validation_status?: 'pending' | 'processing' | 'ai_passed' | 'review_required' | 'validated';
  validation_issues?: Array<{ code: string; severity: 'error' | 'warning'; message: string }>;
  validation_checked_at?: string;
}

interface JsonImportPreview {
  examType: string;
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
  items: Array<Question & { import_index: number; source_url?: string }>;
  issues: Array<{ index: number; severity: 'error' | 'warning'; message: string }>;
}

interface QuestionManagementPageProps {
  embedded?: boolean;
  initialExamType?: string;
  initialQuestionId?: string;
}

const normalizeCorrectAnswers = (value: unknown): number[] => {
  const values: unknown[] = [];
  const collect = (item: unknown) => {
    if (Array.isArray(item)) {
      item.forEach(collect);
      return;
    }

    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (!trimmed) return;

      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          collect(JSON.parse(trimmed));
          return;
        } catch {
          // 쉼표로 구분된 레거시 정답 형식으로 계속 처리합니다.
        }
      }

      trimmed.split(/[,;|\s]+/).forEach((token) => values.push(token));
      return;
    }

    if (item && typeof item === 'object') {
      const dynamoItem = item as { N?: unknown; L?: unknown };
      if (dynamoItem.N !== undefined) {
        collect(dynamoItem.N);
        return;
      }
      if (dynamoItem.L !== undefined) {
        collect(dynamoItem.L);
        return;
      }
    }

    values.push(item);
  };

  collect(value);

  return Array.from(
    new Set(
      values
        .map((item) => (typeof item === 'number' ? item : Number(item)))
        .filter((item) => Number.isInteger(item) && item > 0),
    ),
  ).sort((left, right) => left - right);
};

const hasInvalidOptionNumbers = (options: Question['options']): boolean => {
  const optionNumbers = options.map((option) => option.option_number);
  return (
    new Set(optionNumbers).size !== optionNumbers.length ||
    optionNumbers.some(
      (optionNumber) => !Number.isInteger(optionNumber) || optionNumber < 1 || optionNumber > options.length,
    )
  );
};

const normalizeQuestionOptions = (question: Question): Question => {
  const hasInvalidOptionNumber = hasInvalidOptionNumbers(question.options);
  const storedAnswers = normalizeCorrectAnswers(question.correct_answers);
  const correctAnswers = storedAnswers.length > 0
    ? storedAnswers
    : normalizeCorrectAnswers(question.answer_num);

  if (!hasInvalidOptionNumber) {
    return {
      ...question,
      correct_answers: correctAnswers,
    };
  }

  const remainingAnswers = new Set(correctAnswers);
  const reorderedCorrectAnswers = question.options.reduce<number[]>((answers, option, index) => {
    if (remainingAnswers.delete(option.option_number)) {
      answers.push(index + 1);
    }
    return answers;
  }, []);

  return {
    ...question,
    options: question.options.map((option, index) => ({
      ...option,
      option_number: index + 1,
    })),
    correct_answers: reorderedCorrectAnswers,
  };
};

const QUESTIONS_PER_PAGE = 20;

const QuestionManagementPage: React.FC<QuestionManagementPageProps> = ({
  embedded = false,
  initialExamType,
  initialQuestionId,
}) => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedExamType, setSelectedExamType] = useState(initialExamType || '');
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [creatingQuestion, setCreatingQuestion] = useState<Question | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [examTypes, setExamTypes] = useState<any[]>([]);
  const [questionSearchId, setQuestionSearchId] = useState(initialQuestionId || '');
  const initialQuestionOpenedRef = React.useRef(false);
  const [optionCountFilter, setOptionCountFilter] = useState('all');
  const [validationFilter, setValidationFilter] = useState('all');
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [aiApplyError, setAiApplyError] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingQuestionId, setAnalyzingQuestionId] = useState<string | null>(null);
  const [batchJob, setBatchJob] = useState<AiJob | null>(null);
  const batchWatchRef = React.useRef<AbortController | null>(null);
  const batchRunning = Boolean(batchJob && (batchJob.status === 'queued' || batchJob.status === 'running'));
  const [aiBatchLimit, setAiBatchLimit] = useState(50);
  const [questionModalError, setQuestionModalError] = useState('');
  const [pendingDeleteQuestionId, setPendingDeleteQuestionId] = useState<string | null>(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationPreview, setValidationPreview] = useState<{ total: number; pending: number } | null>(null);
  const [validationLimit, setValidationLimit] = useState(50);
  const [validationAcknowledged, setValidationAcknowledged] = useState(false);
  const [validationConfirmationText, setValidationConfirmationText] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isValidationResetModalOpen, setIsValidationResetModalOpen] = useState(false);
  const [validationResetAcknowledged, setValidationResetAcknowledged] = useState(false);
  const [validationResetConfirmationText, setValidationResetConfirmationText] = useState('');
  const [validationResetError, setValidationResetError] = useState('');
  const [jsonImportPreview, setJsonImportPreview] = useState<JsonImportPreview | null>(null);
  const [isJsonImporting, setIsJsonImporting] = useState(false);
  const [jsonImportError, setJsonImportError] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalQuestions / QUESTIONS_PER_PAGE)), [totalQuestions]);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadQuestions = async (searchId = questionSearchId, page = currentPage) => {
    if (!selectedExamType) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({ examType: selectedExamType });

      if (searchId.trim()) {
        params.set('questionId', searchId.trim());
      } else {
        params.set('limit', String(QUESTIONS_PER_PAGE));
        params.set('offset', String((page - 1) * QUESTIONS_PER_PAGE));
        if (optionCountFilter !== 'all') params.set('optionCount', optionCountFilter);
        if (validationFilter !== 'all') params.set('validated', validationFilter);
      }

      const response = await apiCall(`/admin/questions?${params.toString()}`);
      if (response.ok) {
        const result = await response.json();
        const loadedQuestions = (result.data.questions || []).map((question: Question) => {
          const correctAnswers = normalizeCorrectAnswers(question.correct_answers);
          return {
            ...question,
            correct_answers: correctAnswers.length > 0
              ? correctAnswers
              : normalizeCorrectAnswers(question.answer_num),
          };
        });
        setQuestions(loadedQuestions);
        setTotalQuestions(result.data.pagination?.total || loadedQuestions.length || 0);

        if (
          !initialQuestionOpenedRef.current &&
          initialQuestionId &&
          initialExamType === selectedExamType &&
          searchId.trim() === initialQuestionId &&
          loadedQuestions.length > 0
        ) {
          const reportQuestion = loadedQuestions[0];
          const hadInvalidOptionNumbers = hasInvalidOptionNumbers(reportQuestion.options);
          setQuestionModalError(
            hadInvalidOptionNumbers
              ? '선택지 번호가 중복되거나 올바르지 않아 다시 정렬했습니다. 정답을 확인한 뒤 저장하세요.'
              : '',
          );
          setEditingQuestion(normalizeQuestionOptions(reportQuestion));
          initialQuestionOpenedRef.current = true;
        }
      }
    } catch (error) {
      console.error('Failed to load questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExamTypes = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/admin/exam-types');
      if (response.ok) {
        const result = await response.json();
        const types = result.data || [];
        setExamTypes(types);
        const preferredExamType = initialExamType && types.some((type: { exam_type_id: string }) => type.exam_type_id === initialExamType)
          ? initialExamType
          : types[0]?.exam_type_id || '';
        if (preferredExamType && preferredExamType !== selectedExamType) {
          setSelectedExamType(preferredExamType);
        }
      }
    } catch (error) {
      console.error('Failed to load exam types:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchQuestions = async () => {
    setCurrentPage(1);
    await loadQuestions(questionSearchId, 1);
  };

  const resetQuestionSearch = async () => {
    setQuestionSearchId('');
    setCurrentPage(1);
    await loadQuestions('', 1);
  };

  const updateQuestion = async (question: Question): Promise<boolean> => {
    const normalizedQuestion = {
      ...question,
      correct_answers: normalizeCorrectAnswers(question.correct_answers),
    };
    if (normalizedQuestion.correct_answers.length === 0) {
      setQuestionModalError('정답을 하나 이상 선택하세요.');
      return false;
    }

    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/${question.question_id}`, {
        method: 'PUT',
        body: JSON.stringify(normalizedQuestion),
      });
      if (response.ok) {
        closeQuestionModal();
        await loadQuestions();
        return true;
      }

      const errorResult = await response.json().catch(() => null);
      setQuestionModalError(errorResult?.error?.message || '문제 저장에 실패했습니다.');
      return false;
    } catch (error) {
      console.error('Failed to update question:', error);
      setQuestionModalError('문제 저장 중 네트워크 오류가 발생했습니다.');
      return false;
    }
  };

  const createQuestion = async (question: Question) => {
    const response = await apiCall(`/admin/questions/${selectedExamType}`, {
      method: 'POST',
      body: JSON.stringify(question),
    });
    if (response.ok) {
      closeQuestionModal();
      loadQuestions();
    }
  };

  const deleteQuestion = async (questionId: string) => {
    const response = await apiCall(`/admin/questions/${selectedExamType}/${questionId}`, {
      method: 'DELETE',
    });
    if (response.ok) {
      loadQuestions();
    }
  };

  const openCreateQuestionModal = () => {
    setQuestionModalError('');
    setCreatingQuestion({
      question_id: '',
      question_text: '',
      options: [
        { option_number: 1, option_text: '' },
        { option_number: 2, option_text: '' },
        { option_number: 3, option_text: '' },
        { option_number: 4, option_text: '' },
      ],
      correct_answers: [],
      explanation: '',
      reasoning: '',
      keywords: [],
      validated: false,
    });
  };

  const openEditQuestionModal = (question: Question) => {
    const normalizedQuestion = normalizeQuestionOptions(question);
    const hadInvalidOptionNumbers = hasInvalidOptionNumbers(question.options);

    setAiAnalysis(null);
    setQuestionModalError(
      hadInvalidOptionNumbers
        ? '선택지 번호가 중복되거나 올바르지 않아 다시 정렬했습니다. 정답을 확인한 뒤 저장하세요.'
        : '',
    );
    setEditingQuestion(normalizedQuestion);
  };

  const closeQuestionModal = () => {
    setQuestionModalError('');
    setEditingQuestion(null);
    setCreatingQuestion(null);
  };

  const validateQuestionForSave = (question: Question) => {
    if (!question.question_text.trim()) return '문제 내용을 입력하세요.';
    if (!question.options.length || question.options.some(option => !option.option_text.trim())) return '모든 선택지를 입력하세요.';
    if (question.correct_answers.length === 0) return '정답을 하나 이상 선택하세요.';
    return '';
  };

  const toggleValidation = async (questionId: string, validated: boolean) => {
    const response = await apiCall(`/admin/questions/${selectedExamType}/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify({ validated }),
    });
    if (response.ok) {
      loadQuestions();
    }
  };

  const analyzeQuestionWithAI = async (question: Question) => {
    setAiApplyError('');
    setIsAnalyzing(true);
    setAnalyzingQuestionId(question.question_id);
    try {
      const response = await apiCall('/admin/ai/analyze-question', {
        method: 'POST',
        body: JSON.stringify({
          examType: selectedExamType,
          questionId: question.question_id,
          questionText: question.question_text,
          options: question.options,
          currentAnswers: question.correct_answers,
          explanation: question.explanation,
          reasoning: question.reasoning,
        }),
      });

      const started = await response.json().catch(() => null);
      if (!response.ok || !started?.data?.jobId) {
        alert(started?.error?.message || 'AI 분석을 시작하지 못했습니다.');
        return;
      }

      // 분석은 백그라운드 작업으로 실행되고, 끝날 때까지 상태를 확인합니다 (보통 30초~1분).
      const job = await waitForAiJob<Record<string, unknown>>(apiCall, started.data.jobId);
      if (job.status === 'succeeded' && job.result) {
        setAiAnalysis({ questionId: question.question_id, ...job.result });
      } else {
        alert(job.error ? `AI 분석에 실패했습니다: ${job.error}` : 'AI 분석에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to analyze question:', error);
      alert('AI 분석 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
      setAnalyzingQuestionId(null);
    }
  };

  /** 일괄 작업 진행 상황을 따라가다가 끝나면 목록을 새로 고칩니다. */
  const watchBatchJob = (job: AiJob) => {
    batchWatchRef.current?.abort();
    const controller = new AbortController();
    batchWatchRef.current = controller;
    setBatchJob(job);
    waitForAiJob(apiCall, job.job_id, { onProgress: setBatchJob, intervalMs: 5000, signal: controller.signal })
      .then(() => loadQuestions())
      .catch((error) => {
        if ((error as Error).name !== 'AbortError') console.error('AI 작업 상태 확인 실패:', error);
      });
  };

  const cancelBatchJob = async () => {
    if (!batchJob || !window.confirm('진행 중인 AI 작업을 취소할까요? 지금 처리 중인 문항까지만 저장됩니다.')) return;
    await cancelAiJob(apiCall, batchJob.job_id);
  };

  const applyAiAnalysis = async () => {
    const question = questions.find((item) => item.question_id === aiAnalysis?.questionId);
    const hasCompleteAnalysis =
      aiAnalysis?.explanation && aiAnalysis.explanation !== '설명을 찾을 수 없습니다.' &&
      aiAnalysis?.optionAnalysis && aiAnalysis.optionAnalysis !== '선택지 분석을 찾을 수 없습니다.';

    if (!question || !hasCompleteAnalysis) {
      setAiApplyError('AI 응답이 완성되지 않았거나 원본 문제를 찾을 수 없습니다. AI 분석을 다시 실행하세요.');
      return;
    }

    const updates = {
      ...question,
      correct_answers: normalizeCorrectAnswers(aiAnalysis.recommendedAnswers || question.correct_answers),
      explanation: aiAnalysis.explanation,
      reasoning: aiAnalysis.optionAnalysis,
      keywords: Array.isArray(aiAnalysis.keywords) ? aiAnalysis.keywords : question.keywords,
      question_text: aiAnalysis.translatedQuestionText || question.question_text,
      options: Array.isArray(aiAnalysis.translatedOptions) && aiAnalysis.translatedOptions.length === question.options.length
        ? aiAnalysis.translatedOptions
        : question.options,
    };

    setAiApplyError('');
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/${question.question_id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        setAiApplyError(result?.error?.message || `문제 저장에 실패했습니다. (HTTP ${response.status})`);
        return;
      }

      setAiAnalysis(null);
      await loadQuestions();
    } catch (error) {
      console.error('Failed to apply AI analysis:', error);
      setAiApplyError('AI 분석 결과 저장 중 네트워크 오류가 발생했습니다.');
    }
  };

  const handleJsonImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedExamType) return;
    setJsonImportError('');
    setJsonImportPreview(null);
    setIsJsonImporting(true);
    try {
      const parsed = JSON.parse(await file.text()) as { records?: unknown };
      const response = await apiCall(`/admin/questions/${selectedExamType}/import-json/preview`, {
        method: 'POST',
        body: JSON.stringify({ records: parsed.records }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error?.message || 'JSON 미리보기에 실패했습니다.');
      setJsonImportPreview(result.data);
    } catch (error) {
      setJsonImportError(error instanceof Error ? error.message : 'JSON 파일을 읽지 못했습니다.');
    } finally {
      setIsJsonImporting(false);
    }
  };

  const importJsonQuestions = async () => {
    if (!jsonImportPreview || jsonImportPreview.invalid > 0 || jsonImportPreview.valid === 0) return;
    if (!window.confirm(`정상 문항 ${jsonImportPreview.valid}개를 ${selectedExamType} 문제은행에 추가하시겠습니까? 기존 중복 ID는 건너뜁니다.`)) return;
    setIsJsonImporting(true);
    setJsonImportError('');
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/import-json`, {
        method: 'POST',
        body: JSON.stringify({ items: jsonImportPreview.items, confirmed: true }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error?.message || 'JSON 문제 저장에 실패했습니다.');
      setJsonImportPreview(null);
      alert(`JSON 문제 가져오기 완료\n추가: ${result.data.imported}개\n건너뜀: ${result.data.skipped}개`);
      await loadQuestions();
    } catch (error) {
      setJsonImportError(error instanceof Error ? error.message : 'JSON 문제 저장 중 오류가 발생했습니다.');
    } finally {
      setIsJsonImporting(false);
    }
  };

  const openValidationModal = async () => {
    if (!selectedExamType) return;
    setValidationError('');
    setValidationAcknowledged(false);
    setValidationConfirmationText('');
    setValidationPreview(null);
    setIsValidationModalOpen(true);
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/validation-scan`, { method: 'POST' });
      if (!response.ok) throw new Error('전체 문항 사전 스캔을 완료하지 못했습니다.');
      const result = await response.json();
      setValidationPreview({ total: result.data.total, pending: result.data.pending });
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : '전체 문항 사전 스캔을 완료하지 못했습니다.');
    }
  };

  const openValidationResetModal = () => {
    setValidationResetError('');
    setValidationResetAcknowledged(false);
    setValidationResetConfirmationText('');
    setIsValidationResetModalOpen(true);
  };

  const resetAllValidationStates = async () => {
    if (!selectedExamType || !validationResetAcknowledged || validationResetConfirmationText !== '전체 검수 대기') return;
    setValidationResetError('');
    setIsAnalyzing(true);
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/validation-reset`, {
        method: 'POST',
        body: JSON.stringify({ confirmed: true }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(result?.error?.message || '검수 상태 초기화에 실패했습니다.');
      }
      const result = await response.json();
      setIsValidationResetModalOpen(false);
      alert(`${result.data.reset.toLocaleString()}개 문항을 검수 대기로 초기화했습니다.`);
      await loadQuestions();
    } catch (error) {
      setValidationResetError(error instanceof Error ? error.message : '검수 상태 초기화 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const startBatchValidation = async () => {
    if (!selectedExamType || !validationAcknowledged || validationConfirmationText !== '검수 시작') return;
    setValidationError('');
    setIsAnalyzing(true);
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/validate-batch`, {
        method: 'POST',
        body: JSON.stringify({ confirmed: true, limit: validationLimit }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(result?.error?.message || '문제 일괄 검증에 실패했습니다.');
      }
      const result = await response.json();
      setIsValidationModalOpen(false);
      if (!result.data?.jobId) {
        alert(result.data?.message || '검수 대기 문항이 없습니다.');
        return;
      }
      watchBatchJob({ job_id: result.data.jobId, type: 'validate-batch', status: 'queued', total: result.data.total, counts: { processed: 0, succeeded: 0, failed: 0 }, created_at: '', updated_at: '' });
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : '문제 일괄 검증 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const bulkAnalyze = async () => {
    if (!selectedExamType) return;
    const scopeText = aiBatchLimit === 0 ? '분석 대상 전체' : `${aiBatchLimit}개`;
    if (!window.confirm(`AI를 사용해 선택한 시험 유형의 문제를 ${scopeText} 일괄 분석하시겠습니까? 시간이 오래 걸릴 수 있습니다.`)) return;

    setIsAnalyzing(true);
    try {
      const response = await apiCall('/admin/ai/bulk-analyze', {
        method: 'POST',
        body: JSON.stringify({ examType: selectedExamType, limit: aiBatchLimit }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        alert(result?.error?.message || 'AI 일괄 분석을 시작하지 못했습니다.');
      } else if (!result.data?.jobId) {
        alert(result.data?.message || '분석할 문항이 없습니다.');
      } else {
        watchBatchJob({ job_id: result.data.jobId, type: 'bulk-analyze', status: 'queued', total: result.data.total, counts: { processed: 0, succeeded: 0, failed: 0 }, created_at: '', updated_at: '' });
      }
    } catch (error) {
      console.error('Failed to bulk analyze:', error);
      alert('AI 일괄 분석 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    loadExamTypes();
    return () => batchWatchRef.current?.abort();
  }, []);

  // 시험 유형을 고르면 이전에 시작해 둔 일괄 작업이 아직 돌고 있는지 확인해 진행 표시를 이어 붙입니다.
  useEffect(() => {
    if (!selectedExamType) return;
    batchWatchRef.current?.abort();
    setBatchJob(null);
    findActiveBatchJob(apiCall, selectedExamType).then((job) => {
      if (job) watchBatchJob(job);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExamType]);

  useEffect(() => {
    if (selectedExamType) {
      setCurrentPage(1);
      const initialSearchId = initialExamType === selectedExamType ? initialQuestionId || '' : '';
      setQuestionSearchId(initialSearchId);
      loadQuestions(initialSearchId, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExamType, optionCountFilter, validationFilter]);

  useEffect(() => {
    if (selectedExamType) {
      loadQuestions(questionSearchId, currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  if (!user?.is_admin) {
    return (
      <div className={`admin-page ${embedded ? 'admin-page--embedded' : ''}`}>
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요한 페이지입니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${embedded ? 'admin-page--embedded' : ''}`}>
      {!embedded && (
        <header className="admin-page-title">
          <span className="admin-page-title__eyebrow">문제은행</span>
          <h1>문제 관리</h1>
        </header>
      )}

      {loading && <div className="loading">불러오는 중...</div>}

      <div className="admin-section question-management-panel">
        <div className="admin-header question-management-panel__header">
          <div>
            <h2>문제 목록</h2>
            <p>문제, 선택지, 정답, 해설, 검수 상태를 관리합니다.</p>
          </div>

          <div className="admin-controls question-management-controls">
            <div className="admin-form-inline question-management-controls__group">
              <select className="admin-form-control sm select" value={selectedExamType} onChange={(event) => setSelectedExamType(event.target.value)}>
                <option value="">시험 유형 선택</option>
                {examTypes.map(type => (
                  <option key={type.exam_type_id} value={type.exam_type_id}>{type.name}</option>
                ))}
              </select>
              <select className="admin-form-control sm select" value={optionCountFilter} onChange={(event) => setOptionCountFilter(event.target.value)}>
                <option value="all">모든 선택지</option>
                <option value="4">4개 선택지</option>
                <option value="5">5개 선택지</option>
                <option value="6">6개 선택지</option>
              </select>
              <select className="admin-form-control sm select" value={validationFilter} onChange={(event) => setValidationFilter(event.target.value)}>
                <option value="all">모든 문제</option>
                <option value="true">검수 완료</option>
                <option value="false">검수 대기</option>
              </select>
            </div>

            <div className="admin-form-inline question-management-controls__group">
              <input
                type="text"
                className="admin-form-control sm"
                placeholder="문제 ID로 검색"
                value={questionSearchId}
                onChange={(event) => setQuestionSearchId(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && searchQuestions()}
              />
              <button className="admin-btn secondary" onClick={searchQuestions}>검색</button>
              <button className="admin-btn secondary" onClick={resetQuestionSearch}>전체</button>
              <button className="admin-btn primary" onClick={openCreateQuestionModal}>문제 추가</button>
              <label className="admin-btn secondary json-import-button">
                JSON 업로드
                <input type="file" accept="application/json,.json" onChange={handleJsonImportFile} disabled={isJsonImporting || !selectedExamType} />
              </label>
            </div>
          </div>
        </div>

        {selectedExamType && (
          <div className="bulk-actions">
            <button className="admin-btn danger" onClick={openValidationResetModal} disabled={isAnalyzing || batchRunning}>
              전체 검수 대기 전환
            </button>
            <button className="admin-btn warning" onClick={openValidationModal} disabled={isAnalyzing || batchRunning}>
              문제 일괄 검증
            </button>
            <label className="ai-batch-limit-control">
              <span>AI 분석 수</span>
              <select className="admin-form-control sm select" value={aiBatchLimit} onChange={(event) => setAiBatchLimit(Number(event.target.value))} disabled={isAnalyzing || batchRunning}>
                <option value={0}>전체</option>
                <option value={25}>25개</option>
                <option value={50}>50개</option>
                <option value={100}>100개</option>
                <option value={200}>200개</option>
              </select>
            </label>
            <button className="admin-btn secondary" onClick={bulkAnalyze} disabled={isAnalyzing || batchRunning}>
              {batchRunning ? 'AI 작업 진행 중...' : 'AI 일괄 분석'}
            </button>
          </div>
        )}

        {batchJob && (
          <AiJobProgress job={batchJob} onCancel={cancelBatchJob} onDismiss={() => setBatchJob(null)} />
        )}

        <div className="questions-stats">
          <span>총 {totalQuestions.toLocaleString()}개 문제</span>
          <span>페이지 {currentPage} / {totalPages}</span>
        </div>

        <div className="question-admin-table-wrap">
          <table className="admin-table question-admin-table">
            <thead>
              <tr>
                <th className="col-question-id">ID</th>
                <th className="col-question-text">문제 내용</th>
                <th className="col-option-count">선택지</th>
                <th className="col-correct-answer">정답</th>
                <th className="col-question-meta">자료</th>
                <th className="col-validated">검수</th>
                <th className="col-question-action">작업</th>
              </tr>
            </thead>
            <tbody>
              {questions.map(question => (
                <tr key={question.question_id}>
                  <td className="col-question-id">
                    <span className="admin-badge info question-id-badge" title={question.question_id}>
                      #{question.question_id.split('-').slice(-1)[0] || question.question_id}
                    </span>
                  </td>
                  <td className="col-question-text" title={question.question_text}>
                    <div className="question-text-cell">
                      {question.question_text}
                    </div>
                  </td>
                  <td className="col-option-count">
                    <span className="admin-badge secondary">{question.options?.length || 0}개</span>
                  </td>
                  <td className="col-correct-answer">
                    <div className="correct-answers">
                      {question.correct_answers?.map(answer => (
                        <span key={answer} className="admin-badge success">{answer}</span>
                      ))}
                    </div>
                  </td>
                  <td className="col-question-meta">
                    <div className="admin-flex admin-gap-2">
                      {question.explanation?.trim() && (
                        <span className="question-meta-icon question-meta-icon--explanation" title="해설 있음" aria-label="해설 있음">?</span>
                      )}
                      {question.reasoning?.trim() && (
                        <span className="question-meta-icon question-meta-icon--reasoning" title="근거 있음" aria-label="근거 있음">i</span>
                      )}
                      {question.keywords && question.keywords.length > 0 && (
                        <span className="question-meta-icon question-meta-icon--keywords" title="키워드 있음" aria-label="키워드 있음">#</span>
                      )}
                    </div>
                  </td>
                  <td className="col-validated">
                    <button
                      className={`admin-btn sm ${question.validated ? 'secondary' : 'primary'}`}
                      onClick={() => toggleValidation(question.question_id, !question.validated)}
                    >
                      {question.validated ? '완료' : '대기'}
                    </button>
                  </td>
                  <td className="col-question-action">
                    <div className="admin-flex admin-gap-2">
                      <button className="admin-btn sm primary" onClick={() => openEditQuestionModal(question)}>수정</button>
                      <button className="admin-btn sm secondary" onClick={() => analyzeQuestionWithAI(question)} disabled={isAnalyzing} title="AI 분석은 30초~1분 걸립니다">
                        {analyzingQuestionId === question.question_id ? '분석 중…' : 'AI'}
                      </button>
                      <button className="admin-btn sm danger" onClick={() => setPendingDeleteQuestionId(question.question_id)}>삭제</button>
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && questions.length === 0 && (
                <tr>
                  <td colSpan={7} className="admin-empty-cell">조건에 맞는 문제가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalQuestions > QUESTIONS_PER_PAGE && (
          <div className="admin-pagination">
            <button className="admin-btn secondary sm" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
              이전
            </button>
            <span>{currentPage} / {totalPages}</span>
            <button className="admin-btn secondary sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              다음
            </button>
          </div>
        )}
      </div>

      {aiAnalysis && (
        <div className="question-ai-modal-overlay">
          <div className="question-ai-modal">
            <div className="question-ai-modal__header">
              <h3>AI 분석 결과</h3>
              <button className="question-ai-modal__close" onClick={() => setAiAnalysis(null)} aria-label="닫기">닫기</button>
            </div>
            <div className="question-ai-modal__body">
              <div className="analysis-section">
                <h4>분석 결과</h4>
                <div className="analysis-content">
                  <div className="analysis-item"><strong>신뢰도:</strong> {aiAnalysis.confidence}%</div>
                  <div className="analysis-item">
                    <strong>추천 정답:</strong>
                    <div className="recommended-answers">
                      {aiAnalysis.recommendedAnswers?.map((answer: number) => (
                        <span key={answer} className="admin-badge success">{answer}</span>
                      ))}
                    </div>
                  </div>
                  <div className="analysis-item"><strong>해설:</strong><p>{aiAnalysis.explanation || aiAnalysis.analysis}</p></div>
                  {aiAnalysis.translatedQuestionText && (
                    <div className="analysis-item analysis-item--translation">
                      <strong>한국어 문제 번역:</strong><p>{aiAnalysis.translatedQuestionText}</p>
                      {Array.isArray(aiAnalysis.translatedOptions) && aiAnalysis.translatedOptions.length > 0 && (
                        <ol>
                          {aiAnalysis.translatedOptions.map((option: { option_number: number; option_text: string }) => (
                            <li key={option.option_number}>{option.option_text}</li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                  {aiAnalysis.optionAnalysis && <div className="analysis-item"><strong>근거:</strong><p>{aiAnalysis.optionAnalysis}</p></div>}
                  {aiAnalysis.suggestions && <div className="analysis-item"><strong>개선 제안:</strong><p>{aiAnalysis.suggestions}</p></div>}
                  {Array.isArray(aiAnalysis.documentationSources) && aiAnalysis.documentationSources.length > 0 && (
                    <div className="analysis-item analysis-item--sources">
                      <strong>공식 AWS 문서:</strong>
                      <ul>
                        {aiAnalysis.documentationSources.map((source: { title: string; url: string }) => (
                          <li key={source.url}>
                            <a href={source.url} target="_blank" rel="noreferrer">{source.title || source.url}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="question-ai-modal__footer">
              {aiApplyError && <p className="question-ai-modal__error" role="alert">{aiApplyError}</p>}
              <button className="admin-btn primary" onClick={applyAiAnalysis}>
                분석 결과 적용
              </button>
              <button className="admin-btn secondary" onClick={() => setAiAnalysis(null)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {creatingQuestion && (
        <QuestionModal
          title="문제 추가"
          question={creatingQuestion}
          error={questionModalError}
          onChange={setCreatingQuestion}
          onClose={closeQuestionModal}
          onSave={() => {
            const error = validateQuestionForSave(creatingQuestion);
            if (error) {
              setQuestionModalError(error);
              return;
            }
            createQuestion(creatingQuestion);
          }}
        />
      )}

      {editingQuestion && (
        <QuestionModal
          title="문제 수정"
          question={editingQuestion}
          error={questionModalError}
          onChange={setEditingQuestion}
          onClose={closeQuestionModal}
          onSave={() => {
            const error = validateQuestionForSave(editingQuestion);
            if (error) {
              setQuestionModalError(error);
              return;
            }
            updateQuestion(editingQuestion);
          }}
        />
      )}

      {jsonImportPreview && (
        <div className="question-action-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="json-import-title">
          <div className="question-action-modal json-import-modal">
            <h3 id="json-import-title">JSON 문제 가져오기 미리보기</h3>
            <p className="json-import-modal__summary">
              전체 {jsonImportPreview.total}개 · 정상 {jsonImportPreview.valid}개 · 오류 {jsonImportPreview.invalid}개 · 중복 {jsonImportPreview.duplicates}개
            </p>
            <p>정상 문항만 저장할 수 있습니다. 저장되는 문항은 검수 대기 상태로 시작하며 정답은 JSON의 `model_recommended_answer`를 사용합니다.</p>
            {jsonImportPreview.issues.length > 0 && (
              <div className="json-import-modal__issues">
                {jsonImportPreview.issues.slice(0, 20).map((issue, index) => (
                  <p key={`${issue.index}-${index}`} className={`json-import-modal__issue json-import-modal__issue--${issue.severity}`}>
                    #{issue.index + 1} · {issue.message}
                  </p>
                ))}
                {jsonImportPreview.issues.length > 20 && <p>그 외 {jsonImportPreview.issues.length - 20}개 이슈가 더 있습니다.</p>}
              </div>
            )}
            {jsonImportError && <p className="question-validation-modal__error" role="alert">{jsonImportError}</p>}
            <div className="question-action-modal__footer">
              <button className="admin-btn secondary" onClick={() => setJsonImportPreview(null)} disabled={isJsonImporting}>취소</button>
              <button className="admin-btn primary" onClick={importJsonQuestions} disabled={isJsonImporting || jsonImportPreview.invalid > 0 || jsonImportPreview.valid === 0}>
                {isJsonImporting ? '저장 중...' : '정상 문항 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {jsonImportError && !jsonImportPreview && (
        <div className="question-validation-modal__error" role="alert">{jsonImportError}</div>
      )}

      {isValidationResetModalOpen && (
        <div className="question-action-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="validation-reset-title">
          <div className="question-action-modal question-validation-modal question-validation-modal--danger">
            <h3 id="validation-reset-title">전체 검수 대기 전환</h3>
            <p className="question-validation-modal__warning question-validation-modal__warning--danger">
              이 작업은 <strong>{selectedExamType}</strong>의 모든 문항을 검수 대기 상태로 변경합니다. 진행 중으로 남아 있는 AI 검증 상태와 기존 검수 이슈 기록도 초기화되며, 이후 문제 일괄 검증에서 모든 문항이 AI 대상이 될 수 있습니다. 문항 내용·선택지·정답·해설 자체는 변경하지 않습니다.
            </p>
            <label className="question-validation-modal__check">
              <input type="checkbox" checked={validationResetAcknowledged} onChange={(event) => setValidationResetAcknowledged(event.target.checked)} disabled={isAnalyzing} />
              <span>기존 검수 결과와 이슈 기록이 초기화되며, 모든 문항이 검수 대기로 전환됨을 이해했습니다.</span>
            </label>
            <label className="question-validation-modal__field">
              <span>실행하려면 <strong>전체 검수 대기</strong>를 입력하세요.</span>
              <input className="admin-form-control" value={validationResetConfirmationText} onChange={(event) => setValidationResetConfirmationText(event.target.value)} disabled={isAnalyzing} />
            </label>
            {validationResetError && <p className="question-validation-modal__error" role="alert">{validationResetError}</p>}
            <div className="question-action-modal__footer">
              <button className="admin-btn secondary" onClick={() => setIsValidationResetModalOpen(false)} disabled={isAnalyzing}>취소</button>
              <button className="admin-btn danger" onClick={resetAllValidationStates} disabled={!validationResetAcknowledged || validationResetConfirmationText !== '전체 검수 대기' || isAnalyzing}>
                {isAnalyzing ? '초기화 중...' : '모든 문항 검수 대기 전환'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isValidationModalOpen && (
        <div className="question-action-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="batch-validation-title">
          <div className="question-action-modal question-validation-modal">
            <h3 id="batch-validation-title">문제 일괄 검증 실행</h3>
            <p className="question-validation-modal__warning">
              이 작업은 검수 대기 문항만 {validationLimit === 0 ? '전체' : `최대 ${validationLimit}개`} AI로 검토합니다. 문제 지문·선택지·정답·해설은 자동 수정하지 않지만, 검수 상태와 검토 이슈가 운영 DynamoDB에 저장됩니다.
            </p>
            {validationPreview ? (
              <p className="question-validation-modal__summary">전체 {validationPreview.total.toLocaleString()}개를 규칙 스캔한 결과, AI 검증 대상은 {validationPreview.pending.toLocaleString()}개입니다.</p>
            ) : !validationError && <p>전체 문항을 규칙 기반으로 사전 스캔하고 있습니다…</p>}
            <label className="question-validation-modal__field">
              <span>이번 실행 수</span>
              <select className="admin-form-control" value={validationLimit} onChange={(event) => setValidationLimit(Number(event.target.value))} disabled={isAnalyzing}>
                <option value={0}>전체 (제한 없음)</option>
                <option value={25}>25개</option>
                <option value={50}>50개</option>
                <option value={100}>100개</option>
              </select>
            </label>
            <label className="question-validation-modal__check">
              <input type="checkbox" checked={validationAcknowledged} onChange={(event) => setValidationAcknowledged(event.target.checked)} disabled={isAnalyzing} />
              <span>검수 결과가 운영 데이터에 기록되며, 검토 필요 문항을 제가 확인해야 함을 이해했습니다.</span>
            </label>
            <label className="question-validation-modal__field">
              <span>실행하려면 <strong>검수 시작</strong>을 입력하세요.</span>
              <input className="admin-form-control" value={validationConfirmationText} onChange={(event) => setValidationConfirmationText(event.target.value)} disabled={isAnalyzing} />
            </label>
            {validationError && <p className="question-validation-modal__error" role="alert">{validationError}</p>}
            <div className="question-action-modal__footer">
              <button className="admin-btn secondary" onClick={() => setIsValidationModalOpen(false)} disabled={isAnalyzing}>취소</button>
              <button className="admin-btn warning" onClick={startBatchValidation} disabled={!validationPreview || validationPreview.pending === 0 || !validationAcknowledged || validationConfirmationText !== '검수 시작' || isAnalyzing}>
                {isAnalyzing ? '검증 실행 중...' : '검수 대기 문제 검증'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteQuestionId && (
        <div className="question-action-modal-overlay">
          <div className="question-action-modal">
            <h3>문제 삭제</h3>
            <p>정말 이 문제를 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.</p>
            <div className="question-action-modal__footer">
              <button className="admin-btn secondary" onClick={() => setPendingDeleteQuestionId(null)}>취소</button>
              <button
                className="admin-btn danger"
                onClick={async () => {
                  await deleteQuestion(pendingDeleteQuestionId);
                  setPendingDeleteQuestionId(null);
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionModal: React.FC<{
  title: string;
  question: Question;
  error: string;
  onChange: (question: Question) => void;
  onClose: () => void;
  onSave: () => void;
}> = ({ title, question, error, onChange, onClose, onSave }) => (
  <div className="question-edit-modal-overlay">
    <div className="question-edit-modal">
      <div className="question-edit-modal__header">
        <h3>{title}</h3>
        <button className="question-edit-modal__close" onClick={onClose} aria-label="닫기">닫기</button>
      </div>
      <div className="question-edit-modal__body">
        <QuestionEditor question={question} onChange={onChange} />
        {error && <p className="question-edit-modal__error">{error}</p>}
      </div>
      <div className="question-edit-modal__footer">
        <button className="admin-btn primary" onClick={onSave}>저장</button>
        <button className="admin-btn secondary" onClick={onClose}>취소</button>
      </div>
    </div>
  </div>
);

const QuestionEditor: React.FC<{
  question: Question;
  onChange: (question: Question) => void;
}> = ({ question, onChange }) => {
  const updateQuestion = (updates: Partial<Question>) => {
    onChange({ ...question, ...updates });
  };

  const updateOption = (index: number, text: string) => {
    const newOptions = [...question.options];
    newOptions[index] = { ...newOptions[index], option_text: text };
    updateQuestion({ options: newOptions });
  };

  const addOption = () => {
    if (question.options.length < 6) {
      updateQuestion({
        options: [...question.options, { option_number: question.options.length + 1, option_text: '' }],
      });
    }
  };

  const removeOption = (index: number) => {
    if (question.options.length <= 2) return;

    const reorderedOptions = question.options
      .filter((_, itemIndex) => itemIndex !== index)
      .map((option, itemIndex) => ({ ...option, option_number: itemIndex + 1 }));

    updateQuestion({
      options: reorderedOptions,
      correct_answers: question.correct_answers.filter(answer => answer <= reorderedOptions.length),
    });
  };

  const toggleCorrectAnswer = (optionNumber: number) => {
    const currentAnswers = normalizeCorrectAnswers(question.correct_answers);
    const isCorrect = currentAnswers.includes(optionNumber);
    updateQuestion({
      correct_answers: isCorrect
        ? currentAnswers.filter(answer => answer !== optionNumber)
        : [...currentAnswers, optionNumber].sort(),
    });
  };

  return (
    <div className="question-editor">
      <div className="form-group">
        <label>문제 내용</label>
        <textarea
          className="admin-form-control"
          rows={2}
          value={question.question_text}
          onChange={(event) => updateQuestion({ question_text: event.target.value })}
          placeholder="문제 내용을 입력하세요."
        />
      </div>

      <div className="form-group">
        <label>선택지</label>
        <div className="options-editor">
          {question.options.map((option, index) => (
            <div key={option.option_number} className="option-item">
              <div className="option-header">
                <label>
                  <input
                    type="checkbox"
                    checked={normalizeCorrectAnswers(question.correct_answers).includes(option.option_number)}
                    onChange={() => toggleCorrectAnswer(option.option_number)}
                  />
                  선택지 {option.option_number}
                </label>
                {question.options.length > 2 && (
                  <button type="button" className="admin-btn danger sm" onClick={() => removeOption(index)}>
                    삭제
                  </button>
                )}
              </div>
              <textarea
                className="admin-form-control"
                rows={1}
                value={option.option_text}
                onChange={(event) => updateOption(index, event.target.value)}
                placeholder={`선택지 ${option.option_number} 내용`}
              />
            </div>
          ))}
          {question.options.length < 6 && (
            <button type="button" className="admin-btn secondary" onClick={addOption}>
              선택지 추가
            </button>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>해설</label>
        <textarea
          className="admin-form-control"
          rows={2}
          value={question.explanation}
          onChange={(event) => updateQuestion({ explanation: event.target.value })}
          placeholder="정답에 대한 해설을 입력하세요."
        />
      </div>

      <div className="form-group">
        <label>근거</label>
        <textarea
          className="admin-form-control"
          rows={2}
          value={question.reasoning}
          onChange={(event) => updateQuestion({ reasoning: event.target.value })}
          placeholder="정답 근거를 입력하세요."
        />
      </div>

      <div className="form-group">
        <label>키워드</label>
        <div className="keywords-input">
          <div className="keywords-list">
            {(question.keywords || []).map((keyword, index) => (
              <span key={keyword} className="keyword-tag">
                {keyword}
                <button
                  type="button"
                  onClick={() => {
                    const newKeywords = [...(question.keywords || [])];
                    newKeywords.splice(index, 1);
                    updateQuestion({ keywords: newKeywords });
                  }}
                >
                  삭제
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            placeholder="키워드 입력 후 Enter"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const keyword = event.currentTarget.value.trim();
                if (keyword && !(question.keywords || []).includes(keyword)) {
                  updateQuestion({ keywords: [...(question.keywords || []), keyword] });
                  event.currentTarget.value = '';
                }
              }
            }}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="admin-checkbox-label">
          <input
            type="checkbox"
            checked={question.validated || false}
            onChange={(event) => updateQuestion({ validated: event.target.checked })}
          />
          검수 완료
        </label>
      </div>
    </div>
  );
};

export default QuestionManagementPage;
